import React, { useEffect, useState } from "react";
import { unsubscribe } from "./api";

/**
 * One-click unsubscribe (Outreach spec §9). Reached from the footer of any
 * promotional postcard, and from the List-Unsubscribe header some mail clients
 * surface as their own button. Opting out is honoured immediately — the request
 * fires on load rather than waiting for a confirmation click.
 */
export default function UnsubscribePage({ token }: { token: string }) {
  const [state, setState] = useState<"working" | "done" | "failed">("working");
  const [company, setCompany] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    let alive = true;
    (async () => {
      const res = await unsubscribe(token);
      if (!alive) return;
      setCompany(res.company_name ?? null);
      setState(res.success ? "done" : "failed");
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  return (
    <div className="relative min-h-screen overflow-hidden bg-night-fade font-sans text-frost-100">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 left-1/3 h-72 w-72 rounded-full bg-aurora-cyan/15 blur-[120px]" />
      </div>
      <div className="relative mx-auto flex min-h-screen max-w-xl flex-col items-center justify-center px-5 py-16 text-center">
        <div className="w-full rounded-[2rem] glass p-9 shadow-glass">
          {state === "working" && (
            <div className="mx-auto h-9 w-9 animate-spin rounded-full border-4 border-white/15 border-t-aurora-cyan" />
          )}

          {state === "done" && (
            <>
              <div className="text-4xl">✉️</div>
              <h1 className="mt-4 font-display text-3xl font-semibold text-frost-50">
                You&apos;ve been unsubscribed
              </h1>
              <p className="mt-4 text-frost-200/80">
                {company ? <strong>{company}</strong> : "This address"} will no longer receive
                promotional emails from Winter Desktops.
              </p>
              <p className="mt-3 text-sm text-frost-200/60">
                Any Winter Panda already installed keeps working exactly as before.
              </p>
            </>
          )}

          {state === "failed" && (
            <>
              <h1 className="font-display text-2xl font-semibold text-frost-50">
                We couldn&apos;t process that link
              </h1>
              <p className="mt-4 text-frost-200/80">
                The unsubscribe link may be incomplete. Email{" "}
                <a
                  href="mailto:support@winterdesktops.com"
                  className="text-aurora-cyan hover:text-frost-50"
                >
                  support@winterdesktops.com
                </a>{" "}
                and we&apos;ll take care of it straight away.
              </p>
            </>
          )}

          <a href="/" className="mt-7 inline-block text-sm text-aurora-cyan hover:text-frost-50">
            Visit winterdesktops.com →
          </a>
        </div>
      </div>
    </div>
  );
}
